import type { LineSeriesOption } from 'echarts';
import type { ChartDataItem, LineChartConfig } from '../types';
import { STACKED_AREA_FILL_OPACITY } from './constants';

// Maps a forward series index (0 = first/bottom series) to its palette slot.
// largeToSmall=true assigns color[0] to the highest series (last in the ascending array).
const getColorIndex = (forwardIndex: number, length: number, numColors: number, largeToSmall: boolean): number =>
  largeToSmall ? (length - 1 - forwardIndex) % numColors : forwardIndex % numColors;

export const buildLineSeries = (
  data: ChartDataItem[],
  orderedFields: string[],
  config: LineChartConfig,
): LineSeriesOption[] => {
  const {
    stacked = false,
    largeToSmall = false,
    totalField,
    areaOpacity = STACKED_AREA_FILL_OPACITY,
    lineOpacity = STACKED_AREA_FILL_OPACITY,
    lineWidth = 2,
    curvy = false,
  } = config;
  const colors = config.colorOverrides ?? config.theme.colors;
  const numColors = colors.length;
  const { length } = orderedFields;

  return orderedFields.map((field, i) => {
    const colorIdx = getColorIndex(i, length, numColors, largeToSmall);
    const color = colors[colorIdx];
    const seriesData = data.map((d) => {
      const val = d[field];
      return typeof val === 'number' ? val : null;
    });
    const animOpts =
      config.animated !== false ? { animationDuration: 800, animationEasing: 'quadraticOut' as const } : {};

    // totalField renders as a solid overlay line — not stacked, no area fill.
    if (field === totalField) {
      return {
        name: field,
        type: 'line',
        data: seriesData,
        lineStyle: { color, width: 2, type: 'solid' },
        itemStyle: { color },
        symbol: 'none',
        symbolSize: 0,
        z: 10,
        // silent prevents ECharts' native canvas hit-test from firing mouseover/mouseout on
        // the series elements, which would conflict with our manual dispatchAction in
        // useHoveredStackedAreaField. focus:'series' is still needed so that our highlight
        // dispatch causes all other series to enter blur state.
        silent: true,
        ...animOpts,
        emphasis: { focus: 'series' as const },
        blur: { lineStyle: { opacity: 0.12 } },
      };
    }

    // Show a circle symbol when there's only a single data point so the value is visible.
    const hasSinglePoint = data.filter((d) => typeof d[field] === 'number').length === 1;

    return {
      name: field,
      type: 'line',
      stack: stacked ? 'total' : undefined,
      data: seriesData,
      areaStyle: areaOpacity > 0 ? { opacity: areaOpacity } : undefined,
      lineStyle: { color, width: lineWidth, opacity: lineOpacity },
      // Ring the marker in the paper color so the dot reads on top of the colored area.
      itemStyle: { color, borderColor: config.theme.bgColor, borderWidth: 2 },
      // SIMPL-34452 #4: keep persistent markers hidden (showSymbol:false) except for a lone
      // data point, but define a circle symbol so ECharts still draws the marker at the
      // hovered x (axisPointer), giving a dot at the active point colored to the series/area.
      symbol: 'circle',
      symbolSize: hasSinglePoint ? 10 : 8,
      showSymbol: hasSinglePoint,
      smooth: curvy,
      silent: true,
      ...animOpts,
      emphasis: { focus: 'series' as const },
      blur: { lineStyle: { opacity: 0.16 }, areaStyle: { opacity: 0.16 } },
    };
  });
};
